import {executeSequentially, resolveApiResult} from "../../utils";
import {Button, Col, Glyphicon, Row} from "react-bootstrap";
import React from "react";
import {getLink} from "../../utils/files";
import * as Grid from "../../utils/grid";
import {Field} from "redux-form";
import {FileField} from "../form/form";
import {BaseStatus} from "../../utils/enums";

type ApiResult<T> = {
	isSuccess: boolean,
	data: T,
}

export type ItemContext<T> = {
	itemTag: string,
	itemKeyName: string,
	saveItem: (item: T) => void,
	saveFile: (item: any, file?: File) => Promise<any>,
	showMessage: (message: string, type?: string) => Promise<any>,
	textual: (item: T) => string,
	onSuccess: (item: T) => void
}

export const renderItemFormFiles = (currentFiles, label = "Přílohy") => {
	return <Row className={label && "divider"}>
		<Col sm={12}>
			<Field label={label} name={"newFiles"} type="file" component={FileField}
				currentFiles={currentFiles}
				buttonLabel="Vybrat soubor"
				emptyText={"Prozatím nepřiloženy žádné soubory"}
				multiple={true}
			/>
		</Col>
	</Row>
}

export const renderItemFormButtons = <T>(ctx: ItemContext<T>, item: T, props) => {
	return <Row>
		<Col sm={12}>
			{item[ctx.itemKeyName] && <Button bsStyle="link" onClick={() => {
				props.showModal(
					'Odstranění záznamu',
					'Skutečně trvale odstranit záznam ' + ctx.textual(item) + '?',
					{style: 'danger', label: 'Odstranit', icon: 'trash'},
					() => handleItemSave(ctx, Object.assign(item, {status: 'DELETED'}))
				)
			}
			} disabled={props.submitting}>Odstranit</Button>}
			<Button onClick={props.modal.onHide} disabled={props.submitting}>Storno</Button>
			<Button bsStyle="success" type="submit" disabled={props.submitting}><Glyphicon glyph="ok"
			/> {item[ctx.itemKeyName] ? 'Uložit změny' : 'Založit záznam'}</Button>
		</Col>
	</Row>
}

export const handleItemSave = (ctx: ItemContext, values) => {
	return ctx.saveItem(values).then((json) => {
		const res = parseApiResult(ctx, json);

		if (res.isSuccess) {
			saveFilesIfNeeded(ctx, values.newFiles, res.data)
		}

		return new Promise((resolve) => resolve(res.data))
	})
}

export const onItemSaveSuccess = (d, props) => {
	if (props.modal) {
		if (props.modal.onSuccess) {
			props.modal.onSuccess(d);
		}
		if (props.modal.onHide) {
			props.modal.onHide();
		}
	}
}

export const parseApiResult = <T>(ctx: ItemContext, json): ApiResult<T> => {
	if (!resolveApiResult(json)) {
		// error
		const m = ctx.itemTag + ': Uložení selhalo: ' + (json.error || JSON.stringify(json));
		ctx.showMessage(m, 'error');
		return {
			isSuccess: false,
			data: null
		};
	}
	let m = json.response.message;
	const d = json.response.data;
	let t = ctx.itemTag + ' ' + ctx.textual(d);
	if (m === 'NO_CHANGE') {
		m = t + ': Nebyly provedeny žádné změny';

	} else if (m === 'UPDATED') {
		let isDelete = false;
		if (d.status === 'DELETED' && d.changes && d.changes.length) {
			for (let i = 0; i < d.changes.length; i++) {
				const change = d.changes[i];
				if (change.newValue === BaseStatus.DELETED) {
					isDelete = true;
					break;
				}
			}
		}
		if (isDelete) {
			m = t + ': Úspěšně odstraněno';
		} else {
			m = t + ': Změny úspěšně uloženy';
		}

	} else if (m === 'CREATED') {
		m = t + ': Úspěšně založeno';

	} else if (m === 'DELETED') {
		m = t + ': Úspěšně odstraněno';
	}

	ctx.showMessage(m);
	return {
		isSuccess: true,
		data: d
	};
}

export const saveFilesIfNeeded = (ctx: ItemContext, newFiles, item) => {
	if (newFiles && newFiles.length > 0) {
		const calls = [];
		newFiles.forEach((file) => {
			let fileItem;
			if (file.fileId && file.isRemove) {
				fileItem = file;
			} else {
				fileItem = {
					dogId: item.dogId,
				};
				fileItem[ctx.itemKeyName] = item[ctx.itemKeyName];
			}

			calls.push(() => ctx.saveFile(fileItem, file).then((json) => {
				parseApiResult(createFileContext(ctx), json);
				return json;
			}));
		});
		executeSequentially(calls).then(() => {
			ctx.onSuccess(item);
		});

	} else {
		ctx.onSuccess(item);
	}
}

const createFileContext = (ctx: ItemContext): ItemContext => {
	return {
		...ctx,
		itemTag: 'Příloha',
		itemKeyName: 'fileId',
		textual: (f) => f.fileName || f.fileId
	}
}

export const formatItemActions = (item, itemKeyName, files, handleItemModalClick, withClone = false) => {
	let fileCount = 0;
	if (files && files.length > 0) {
		fileCount = files.filter((f) => f[itemKeyName] === item[itemKeyName]).length;
	}
	return <span className={'btn-group'}>
			<Button bsSize="xs" onClick={() => handleItemModalClick(item[itemKeyName])}><Glyphicon glyph="pencil"/> {fileCount || ''}</Button>
			{withClone && <Button bsSize="xs" onClick={() => handleItemModalClick(item[itemKeyName], 'clone', item)}><Glyphicon glyph="duplicate"/> </Button>}
		</span>
}


export type ItemTableContext<T> = {
	itemKeyName: string,
	cols: Array<{
		title: string,
		width: number
	}>,
	renderRow: (item: T) => HTMLElement,
}

export const renderItemTable = (tableCtx: ItemTableContext, props) => {
	const {isFetchingItems, items, files} = props;

	const rows = [];
	if (isFetchingItems && items === null) {
		rows.push(<tr key="none">
			<td colSpan={5}> {Grid.renderIsFetchingData()}</td>
		</tr>);
	}
	if (items && items.length) {
		for (let i = 0; i < items.length; i++) {
			const item = items[i];
			rows.push(tableCtx.renderRow(item, files, props));
			if (!files || !files.length) {
				continue;
			}
			rows.push(<tr key={'files-' + item[tableCtx.itemKeyName]}>
				<td className={'table-item-files'} colSpan={5}>{files.filter((f) => f[tableCtx.itemKeyName] === item[tableCtx.itemKeyName]).map((f) => {
					let fileName = f.fileName;
					if (fileName && fileName.length > 35) {
						fileName = fileName.substring(0, 17).trim() + '...' + fileName.substring(fileName.length - 18).trim();
					}
					return <a target={'_blank'} key={f.fileId} href={getLink(f.guid, f.fileName)}>{fileName}</a>;
				})}</td>
			</tr>)
		}
	}
	if (!rows.length) {
		rows.push(<tr key="none">
			<td colSpan={5} className="text-center text-muted">Prozatím žádné záznamy</td>
		</tr>)
	}

	return <table className={"table table-items " + (files && files.length > 0 ? 'table-striped2' : 'table-striped')}>
		<colgroup>
			{tableCtx.cols.map((col, i) => <col key={i} width={col.width + '%'}/>)}
			<col width={(100 - tableCtx.cols.reduce((sum, col) => col.width + sum, 0) + '%')}/>
		</colgroup>
		<thead>
		<tr key="head">
			{tableCtx.cols.map((col, i) => <th key={i}>{col.title}</th>)}
			<th>{isFetchingItems && Grid.renderIsFetchingData('')}</th>
		</tr>
		</thead>
		<tbody>
		{rows}
		</tbody>
	</table>;
}



// WEBPACK FOOTER //
// ./src/components/dog/helpers.js